import React, { useState } from 'react';
import { Upload, Button, Progress, message, Typography } from 'antd';
import { UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { apiService } from '@/services/api';

const { Text } = Typography;

interface MediaUploaderProps {
  accept?: string;
  maxSize?: number; // in bytes
  onFileSelect?: (file: File) => void;
  onUploadSuccess?: (response: any) => void;
  onUploadError?: (error: any) => void;
  multiple?: boolean;
  disabled?: boolean;
  autoUpload?: boolean; // Enable/disable auto-upload
}

export const MediaUploader: React.FC<MediaUploaderProps> = ({
  accept = '*/*',
  maxSize = 53687091200, // 50GB default (50 * 1024 * 1024 * 1024)
  onFileSelect,
  onUploadSuccess,
  onUploadError,
  multiple = false,
  disabled = false,
  autoUpload = true // Default to true for backward compatibility
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const beforeUpload = (file: File) => {
    // Check file size
    if (file.size > maxSize) {
      message.error(`Le fichier ne doit pas dépasser ${formatFileSize(maxSize)}`);
      return false;
    }

    // Check file type
    const isValidType = accept === '*/*' || file.type.match(new RegExp(accept.replace('*', '.*')));
    if (!isValidType) {
      message.error(`Type de fichier non supporté. Types acceptés: ${accept}`);
      return false;
    }

    return false; // Prevent auto upload
  };

  const handleFileSelect = async (file: File) => {
    setUploadedFile(file);
    onFileSelect?.(file);

    // Auto upload for media files only if autoUpload is enabled
    if (autoUpload && (file.type.startsWith('video/') || file.type.startsWith('image/'))) {
      await handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const contentType = getContentType(file.type);
      const response = await apiService.uploadMedia(file, contentType);

      setUploadProgress(100);
      message.success('Fichier téléchargé avec succès');
      onUploadSuccess?.(response);
    } catch (error: any) {
      message.error(`Erreur lors du téléchargement: ${error.message}`);
      onUploadError?.(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const getContentType = (fileType: string): string => {
    if (fileType.startsWith('video/')) return 'VIDEO';
    if (fileType.startsWith('image/')) return 'IMAGE';
    if (fileType.startsWith('audio/')) return 'AUDIO';
    return 'OTHER';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const customRequest = ({ file, onSuccess, onError }: any) => {
    handleFileSelect(file as File);
    onSuccess();
  };

  return (
    <div>
      <Upload
        accept={accept}
        beforeUpload={beforeUpload}
        customRequest={customRequest}
        multiple={multiple}
        disabled={disabled}
        showUploadList={false}
      >
        <Button
          icon={<UploadOutlined />}
          loading={uploading}
          disabled={disabled}
        >
          {uploadedFile ? 'Changer le fichier' : 'Sélectionner un fichier'}
        </Button>
      </Upload>

      {/* Only show file info if autoUpload is true (auto-upload mode) */}
      {autoUpload && uploadedFile && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <Text type="success">{uploadedFile.name}</Text>
            <Text type="secondary">({formatFileSize(uploadedFile.size)})</Text>
          </div>

          {uploading && (
            <div style={{ marginTop: 8 }}>
              <Progress
                percent={uploadProgress}
                size="small"
                status={uploadProgress === 100 ? 'success' : 'active'}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
